import { Action } from "@/api-schema-enums";
import { createForUser } from "@/casl-provider";
import { useCarRunnerScoreSubmitMutation } from "@/common/api/car-runner-score/use-car-runner-score-submit.mutation";
import { VEHICLE_SHAPE } from "@/components/ui/vehicle-loader/vehicle-outline";
import { userStore } from "@/store/user-store";
import {
  useEffect,
  useEffectEvent,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type ReactElement,
  type ReactNode,
  type SVGProps,
} from "react";
import { useTranslation } from "react-i18next";
import carSvg from "../_assets/car-crashed.svg?raw";
import {
  CAR_BOX,
  CAR_X,
  createRunner,
  DUCK_SCALE,
  GROUND_Y,
  jump,
  readHighScore,
  RUNNER_HEIGHT,
  RUNNER_WIDTH,
  runnerScore,
  saveHighScore,
  stepRunner,
  type Obstacle,
  type RunnerState,
} from "../_helpers/car-runner";
import { CAR_RUNNER_SCORE_SUBJECT } from "../_helpers/car-runner.permissions";
import { CarRunnerLeaderboard } from "./car-runner-leaderboard";

const CAR_PATHS = [...carSvg.matchAll(/\sd="([^"]+)"/g)].map(([, path]) => path);
// The first two paths of car-crashed.svg are the car itself; only its smoke marks are reused for a crash.
const SMOKE_PATHS = CAR_PATHS.slice(2);
const STEP = 1 / 120;
const RESTART_DELAY_MS = 500;

function isJumpKey(event: KeyboardEvent): boolean {
  return event.key === " " || event.key === "ArrowUp";
}

/** Only react to keys nobody else wants, e.g. not while a button or input is focused. */
function isGameTarget(event: KeyboardEvent, container: HTMLElement | null): boolean {
  if (event.ctrlKey || event.metaKey || event.altKey) return false;
  const { target } = event;
  return (
    target === document.body ||
    target === document.documentElement ||
    (target instanceof Node && Boolean(container?.contains(target)))
  );
}

function drawObstacle(ctx: CanvasRenderingContext2D, obstacle: Obstacle): void {
  const { x, width, top, bottom, count } = obstacle;
  if (obstacle.kind === "cone") {
    for (let index = 0; index < count; index++) {
      const left = x + index * 16;
      ctx.fillRect(left, bottom - 3, 16, 3);
      ctx.beginPath();
      ctx.moveTo(left + 3, bottom - 3);
      ctx.lineTo(left + 8, top);
      ctx.lineTo(left + 13, bottom - 3);
      ctx.fill();
      ctx.clearRect(left + 5, top + 11, 6, 3);
    }
    return;
  }
  if (obstacle.kind === "tires") {
    const tireHeight = (bottom - top) / count;
    for (let index = 0; index < count; index++) {
      const tireTop = bottom - (index + 1) * tireHeight;
      ctx.beginPath();
      ctx.roundRect(x, tireTop, width, tireHeight - 1, 4);
      ctx.fill();
      ctx.clearRect(x + width / 2 - 4, tireTop + tireHeight / 2 - 2, 8, 2);
    }
    return;
  }
  ctx.fillRect(x + 6, top, 1, bottom - 10);
  ctx.fillRect(x + width - 7, top, 1, bottom - 10);
  ctx.fillRect(x, bottom - 10, width, 10);
  for (let stripe = x + 4; stripe < x + width - 4; stripe += 11) ctx.clearRect(stripe, bottom - 8, 5, 6);
}

interface CarRunnerProps {
  icon: ComponentType<SVGProps<SVGSVGElement>>;
}

interface CarRunnerGameProps extends CarRunnerProps {
  onRunEnd?: (score: number) => void;
  footer?: ReactNode;
}

export function CarRunner({ icon }: CarRunnerProps): ReactElement {
  const user = userStore((state) => state.user);
  const canUseLeaderboard = useMemo(() => createForUser(user).can(Action.Read, CAR_RUNNER_SCORE_SUBJECT), [user]);

  return canUseLeaderboard ? <CarRunnerWithLeaderboard icon={icon} /> : <CarRunnerGame icon={icon} />;
}

function CarRunnerWithLeaderboard({ icon }: CarRunnerProps): ReactElement {
  const { mutate: submitScore } = useCarRunnerScoreSubmitMutation();

  return (
    <CarRunnerGame
      icon={icon}
      onRunEnd={(score) => {
        if (score > 0) submitScore({ score });
      }}
      footer={<CarRunnerLeaderboard />}
    />
  );
}

function CarRunnerGame({ icon: Icon, onRunEnd, footer }: CarRunnerGameProps): ReactElement {
  const { t } = useTranslation("common");
  const [isPlaying, setIsPlaying] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const reportRunEnd = useEffectEvent((score: number) => onRunEnd?.(score));

  useEffect(() => {
    if (isPlaying) return;
    const keydown = (event: KeyboardEvent) => {
      if (event.repeat || event.key !== " " || !isGameTarget(event, containerRef.current)) return;
      event.preventDefault();
      setIsPlaying(true);
    };
    window.addEventListener("keydown", keydown);
    return () => window.removeEventListener("keydown", keydown);
  }, [isPlaying]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!isPlaying || !canvas || !ctx) return;

    const bodyPaths = VEHICLE_SHAPE.bodyPaths.map((path) => new Path2D(path));
    const smokePaths = SMOKE_PATHS.map((path) => new Path2D(path));
    const style = getComputedStyle(canvas);
    const gameOverText = t("RunnerGameOver");
    const restartText = t("RunnerGameRestart");
    const highScoreText = t("RunnerGameHighScore");
    let state: RunnerState = createRunner();
    let highScore = readHighScore();
    let crashedAt = 0;
    let isKeyDucking = false;
    let isPointerDucking = false;
    let frame = 0;
    let last = 0;
    let accumulator = 0;

    const act = (isRepeat: boolean) => {
      if (state.status === "running") {
        jump(state);
        return;
      }
      if (isRepeat || performance.now() - crashedAt < RESTART_DELAY_MS) return;
      state = createRunner();
    };

    const draw = () => {
      const color = (name: string) => style.getPropertyValue(name).trim() || "#000";
      ctx.setTransform(canvas.width / RUNNER_WIDTH, 0, 0, canvas.height / RUNNER_HEIGHT, 0, 0);
      ctx.clearRect(0, 0, RUNNER_WIDTH, RUNNER_HEIGHT);

      ctx.fillStyle = color("--muted-foreground");
      ctx.fillRect(0, GROUND_Y, RUNNER_WIDTH, 1.5);
      for (let x = -(state.distance % 48); x < RUNNER_WIDTH; x += 48) ctx.fillRect(x, GROUND_Y + 8, 22, 2);

      ctx.font = "bold 12px ui-monospace, SFMono-Regular, Menlo, monospace";
      ctx.textAlign = "right";
      const pad = (value: number) => String(value).padStart(5, "0");
      ctx.fillText(`${highScoreText} ${pad(highScore)}   ${pad(runnerScore(state))}`, RUNNER_WIDTH - 8, 18);

      ctx.fillStyle = color("--foreground");
      for (const obstacle of state.obstacles) drawObstacle(ctx, obstacle);

      const isCrashed = state.status === "crashed";
      const squash = state.ducking ? DUCK_SCALE : 1;
      const toCanvas = { a: 1, b: 0, c: 0, d: squash, e: CAR_X, f: GROUND_Y - state.y - CAR_BOX.bottom * squash };
      const stroke = (paths: Path2D[], lineWidth: number) => {
        const combined = new Path2D();
        for (const path of paths) combined.addPath(path, toCanvas);
        ctx.lineWidth = lineWidth;
        ctx.stroke(combined);
      };
      const { wheelXs, wheelY, wheelRadius, spokeAngles, spokeLength } = VEHICLE_SHAPE;
      const wheelTurn = state.distance / wheelRadius;
      const wheels = new Path2D();
      const spokes = new Path2D();
      for (const x of wheelXs) {
        wheels.moveTo(x + wheelRadius, wheelY);
        wheels.arc(x, wheelY, wheelRadius, 0, 2 * Math.PI);
        for (const angle of spokeAngles) {
          const radians = (angle * Math.PI) / 180 + wheelTurn;
          spokes.moveTo(x, wheelY);
          spokes.lineTo(x + Math.cos(radians) * spokeLength, wheelY + Math.sin(radians) * spokeLength);
        }
      }

      ctx.strokeStyle = color("--primary");
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      stroke([...bodyPaths, wheels], 2);
      stroke([spokes], 1.2);
      if (isCrashed) {
        ctx.save();
        ctx.transform(toCanvas.a, toCanvas.b, toCanvas.c, toCanvas.d, toCanvas.e, toCanvas.f);
        ctx.fillStyle = color("--primary");
        for (const path of smokePaths) ctx.fill(path);
        ctx.restore();
      }

      if (!isCrashed) return;
      ctx.textAlign = "center";
      ctx.fillStyle = color("--foreground");
      ctx.font = "bold 18px ui-monospace, SFMono-Regular, Menlo, monospace";
      ctx.fillText(gameOverText, RUNNER_WIDTH / 2, 60);
      ctx.fillStyle = color("--muted-foreground");
      ctx.font = "12px ui-monospace, SFMono-Regular, Menlo, monospace";
      ctx.fillText(restartText, RUNNER_WIDTH / 2, 82);
    };

    const tick = (now: number) => {
      // Capped so a backgrounded tab does not fast-forward into the next obstacle.
      accumulator += last ? Math.min((now - last) / 1000, 0.05) : 0;
      last = now;
      const wasRunning = state.status === "running";
      while (accumulator >= STEP && state.status === "running") {
        stepRunner(state, isKeyDucking || isPointerDucking, STEP);
        accumulator -= STEP;
      }
      if (wasRunning && state.status === "crashed") {
        crashedAt = performance.now();
        if (runnerScore(state) > highScore) {
          highScore = runnerScore(state);
          saveHighScore(highScore);
        }
        reportRunEnd(runnerScore(state));
      }
      if (state.status === "crashed") accumulator = 0;
      draw();
      frame = requestAnimationFrame(tick);
    };

    const resize = () => {
      const ratio = window.devicePixelRatio || 1;
      canvas.width = Math.round(canvas.clientWidth * ratio);
      canvas.height = Math.round(canvas.clientHeight * ratio);
    };
    const keydown = (event: KeyboardEvent) => {
      if (!isGameTarget(event, containerRef.current)) return;
      if (event.key === "ArrowDown") {
        event.preventDefault();
        isKeyDucking = true;
        return;
      }
      if (!isJumpKey(event)) return;
      event.preventDefault();
      act(event.repeat);
    };
    const keyup = (event: KeyboardEvent) => {
      if (event.key === "ArrowDown") isKeyDucking = false;
    };
    const pointerdown = (event: PointerEvent) => {
      event.preventDefault();
      const rect = canvas.getBoundingClientRect();
      if (state.status === "running" && event.clientY - rect.top > rect.height / 2) isPointerDucking = true;
      else act(false);
    };
    const release = () => {
      isKeyDucking = false;
      isPointerDucking = false;
    };

    resize();
    window.addEventListener("resize", resize);
    window.addEventListener("keydown", keydown);
    window.addEventListener("keyup", keyup);
    window.addEventListener("pointerup", release);
    window.addEventListener("pointercancel", release);
    window.addEventListener("blur", release);
    canvas.addEventListener("pointerdown", pointerdown);
    frame = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", resize);
      window.removeEventListener("keydown", keydown);
      window.removeEventListener("keyup", keyup);
      window.removeEventListener("pointerup", release);
      window.removeEventListener("pointercancel", release);
      window.removeEventListener("blur", release);
      canvas.removeEventListener("pointerdown", pointerdown);
    };
  }, [isPlaying, t]);

  if (!isPlaying) {
    return (
      <div className="flex justify-center">
        <Icon className="fill-primary/90 relative h-32 w-48 filter sm:h-56 sm:w-96" />
      </div>
    );
  }

  return (
    <div ref={containerRef} className="flex flex-col items-center gap-2">
      <canvas
        ref={canvasRef}
        tabIndex={0}
        role="img"
        aria-label={t("RunnerGameDescription")}
        className="focus-visible:ring-ring aspect-[4/1] w-full max-w-2xl touch-none outline-none select-none focus-visible:ring-2"
      />
      <p className="text-muted-foreground text-xs">{t("RunnerGameHint")}</p>
      {footer}
    </div>
  );
}
